/**
 * Canonical 3-level category taxonomy, per "CurbNTurf category taxonomy —
 * unify host / data / server". Level 1 (PersonaCategory) is the guest-facing
 * bucket; Level 2 (PrimaryPropertyType) is the host's single wizard pick,
 * rolling up to exactly one Level 1 bucket via PRIMARY_PROPERTY_TYPE_TO_CATEGORY;
 * Level 3 (PropertyDescriptor) is a multi-select setting/landscape tag list,
 * decoupled from Category.
 *
 * Enum keys match their interface's string-literal union exactly (camelCase),
 * mirroring LandType/ILandType — validation code elsewhere reads `Object.keys(Enum)`
 * as the source of truth for valid slugs.
 */

export enum PersonaCategory {
  farm = 'Farms',
  winery = 'Wineries',
  brewery = 'Breweries',
  driveway = 'Driveways',
  attraction = 'Attractions',
  nature = 'Nature',
}

export interface IPersonaCategoryType {
  type: 'farm' | 'winery' | 'brewery' | 'driveway' | 'attraction' | 'nature';
}

export enum PrimaryPropertyType {
  farm = 'Farm',
  orchard = 'Orchard',
  specialtyFarm = 'Specialty Farm',
  ranch = 'Ranch',
  winery = 'Winery',
  brewery = 'Brewery',
  distillery = 'Distillery',
  privateHome = 'Private Home',
  rvPark = 'RV Park',
  golfCourse = 'Golf Course',
  church = 'Church',
  woodlands = 'Woodlands',
  custom = 'Custom',
}

export interface IPrimaryPropertyType {
  type:
    | 'farm'
    | 'orchard'
    | 'specialtyFarm'
    | 'ranch'
    | 'winery'
    | 'brewery'
    | 'distillery'
    | 'privateHome'
    | 'rvPark'
    | 'golfCourse'
    | 'church'
    | 'woodlands'
    | 'custom';
}

/** Level 2 -> Level 1 rollup — each PrimaryPropertyType maps to exactly one PersonaCategory. */
export const PRIMARY_PROPERTY_TYPE_TO_CATEGORY: Record<IPrimaryPropertyType['type'], IPersonaCategoryType['type']> = {
  farm: 'farm',
  orchard: 'farm',
  specialtyFarm: 'farm',
  ranch: 'farm',
  winery: 'winery',
  brewery: 'brewery',
  distillery: 'brewery',
  privateHome: 'driveway',
  rvPark: 'attraction',
  golfCourse: 'attraction',
  church: 'attraction',
  woodlands: 'nature',
  custom: 'attraction',
};

export function getCategoryForType(t: IPrimaryPropertyType['type']): IPersonaCategoryType['type'] {
  return PRIMARY_PROPERTY_TYPE_TO_CATEGORY[t];
}

/** Venue-type LandType values considered when backfilling primaryType from legacy landTypes[]. */
export const VENUE_TYPE_LAND_TYPES: IPrimaryPropertyType['type'][] = [
  'winery',
  'brewery',
  'distillery',
  'farm',
  'orchard',
];

export enum PropertyDescriptor {
  backyard = 'Backyard',
  curbside = 'Curbside',
  driveway = 'Driveway',
  urban = 'Urban',
  suburban = 'Suburban',
  forest = 'Forest',
  mountains = 'Mountains',
  stream = 'Stream',
  plain = 'Plain',
  lake = 'Lake',
  desert = 'Desert',
  canyon = 'Canyon',
  wetlands = 'Wetlands',
  coastal = 'Coastal',
  beach = 'Beach',
  hotSpring = 'Hot Spring',
  waterfall = 'Waterfall',
}

export interface IPropertyDescriptor {
  type:
    | 'backyard'
    | 'curbside'
    | 'driveway'
    | 'urban'
    | 'suburban'
    | 'forest'
    | 'mountains'
    | 'stream'
    | 'plain'
    | 'lake'
    | 'desert'
    | 'canyon'
    | 'wetlands'
    | 'coastal'
    | 'beach'
    | 'hotSpring'
    | 'waterfall';
}

export const DESCRIPTOR_GROUP: Record<IPropertyDescriptor['type'], 'setting' | 'landscape'> = {
  backyard: 'setting',
  curbside: 'setting',
  driveway: 'setting',
  urban: 'setting',
  suburban: 'setting',
  forest: 'landscape',
  mountains: 'landscape',
  stream: 'landscape',
  plain: 'landscape',
  lake: 'landscape',
  desert: 'landscape',
  canyon: 'landscape',
  wetlands: 'landscape',
  coastal: 'landscape',
  beach: 'landscape',
  hotSpring: 'landscape',
  waterfall: 'landscape',
};

/** All Level 3 descriptor slugs — every LandType value that isn't a venue type
 *  (VENUE_TYPE_LAND_TYPES) is one of these, spelled identically to ILandType['type']. */
export const ALL_PROPERTY_DESCRIPTORS: IPropertyDescriptor['type'][] = Object.keys(
  DESCRIPTOR_GROUP,
) as IPropertyDescriptor['type'][];
